import React, { useContext, useEffect, useMemo, useState } from "react";
import { Link, useHistory } from "react-router-dom";
import {
	Bar,
	BarChart,
	CartesianGrid,
	Cell,
	Pie,
	PieChart,
	ResponsiveContainer,
	Tooltip,
	XAxis,
	YAxis,
} from "recharts";
import { AuthContext } from "../../../context/Auth/AuthContext";
import { ThemeContext } from "../../../context/Theme/ThemeContext";
import { ROLES } from "../../../constants/roles";
import api from "../../../services/api";
import toastError from "../../../errors/toastError";
import { i18n } from "../../../translate/i18n";
import MasterPageBreadcrumb from "../../../layout/master/MasterPageBreadcrumb";
import { getMasterRouteMeta } from "../../../layout/master/masterRouteMeta";
import MasterStatCard from "../../../components/Master/MasterStatCard";
import MasterSpinner from "../../../components/Master/MasterSpinner";
import { MASTER_HOME } from "../../../helpers/masterUser";
import MasterProgressMetric from "../../../components/Master/MasterProgressMetric";
import { buildSystemMetricSummary } from "../../../helpers/systemMetrics";

function aggregateCompanies(companies) {
	const total = companies.length;
	const active = companies.filter((c) => c.isActive !== false).length;
	const inactive = total - active;
	const totalUsers = companies.reduce((s, c) => s + (c.userCount || 0), 0);
	const totalConnections = companies.reduce((s, c) => s + (c.connectionCount || 0), 0);

	const byMonth = {};
	companies.forEach((c) => {
		if (!c.createdAt) return;
		const d = new Date(c.createdAt);
		const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
		byMonth[key] = (byMonth[key] || 0) + 1;
	});
	const registrationsByMonth = Object.entries(byMonth)
		.sort(([a], [b]) => a.localeCompare(b))
		.slice(-12)
		.map(([month, count]) => ({ month, count }));

	const topByUsers = [...companies]
		.sort((a, b) => (b.userCount || 0) - (a.userCount || 0))
		.slice(0, 8)
		.map((c) => ({
			name: (c.name || `#${c.id}`).slice(0, 18),
			users: c.userCount || 0,
		}));

	const statusPie = [
		{ name: i18n.t("master.dashboard.active"), value: active },
		{ name: i18n.t("master.dashboard.inactive"), value: inactive },
	].filter((x) => x.value > 0);

	return {
		total,
		active,
		inactive,
		totalUsers,
		totalConnections,
		registrationsByMonth,
		topByUsers,
		statusPie,
	};
}

const PIE_COLORS = ["#22c55e", "#f97316"];

export default function MasterDashboard() {
	const history = useHistory();
	const { user, loading: authLoading } = useContext(AuthContext);
	const { darkMode } = useContext(ThemeContext);
	const [companies, setCompanies] = useState([]);
	const [systemInfo, setSystemInfo] = useState(null);
	const [loading, setLoading] = useState(true);
	const routeMeta = getMasterRouteMeta(MASTER_HOME);
	const chartText = darkMode ? "#a1a1aa" : "#71717a";
	const chartGrid = darkMode ? "#3f3f46" : "#e4e4e7";

	useEffect(() => {
		if (authLoading) return;
		if (!user || user.profile !== ROLES.master) {
			history.replace("/");
			return;
		}
		Promise.all([
			api.get("/master/companies"),
			api.get("/master/system-info").catch(() => ({ data: null })),
		])
			.then(([companiesRes, systemRes]) => {
				setCompanies(companiesRes.data || []);
				setSystemInfo(systemRes.data);
			})
			.catch((err) => toastError(err))
			.finally(() => setLoading(false));
	}, [user, authLoading, history]);

	const stats = useMemo(() => aggregateCompanies(companies), [companies]);
	const systemSummary = useMemo(() => buildSystemMetricSummary(systemInfo), [systemInfo]);

	if (authLoading || !user || user.profile !== ROLES.master) {
		return null;
	}

	const IconUsers = () => (
		<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
			<path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
			<circle cx="9" cy="7" r="4" />
			<path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
		</svg>
	);
	const IconBuilding = () => (
		<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
			<path d="M3 21h18M5 21V7l8-4v18M19 21V11l-6-3" />
		</svg>
	);
	const IconWifi = () => (
		<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
			<path d="M5 12.55a11 11 0 0 1 14.08 0M1.42 9a16 16 0 0 1 21.16 0M8.53 16.11a6 6 0 0 1 6.95 0" />
			<line x1="12" y1="20" x2="12.01" y2="20" />
		</svg>
	);
	const IconCheck = () => (
		<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
			<polyline points="20 6 9 17 4 12" />
		</svg>
	);

	return (
		<div className="mx-auto w-full max-w-7xl box-border">
			<MasterPageBreadcrumb
				title={i18n.t(routeMeta.titleKey)}
				breadcrumbs={routeMeta.breadcrumbs}
			/>
			{routeMeta.subtitleKey && (
				<p className="-mt-2 mb-6 text-sm text-default-500 dark:text-default-400">
					{i18n.t(routeMeta.subtitleKey)}
				</p>
			)}

			{loading ? (
				<div className="master-loading">
					<MasterSpinner />
				</div>
			) : (
				<>
					<div className="mb-6 grid grid-cols-1 gap-5 sm:grid-cols-2 xl:grid-cols-4">
						<MasterStatCard
							tone="success"
							icon={<IconBuilding />}
							value={stats.total}
							label={i18n.t("master.dashboard.totalCompanies")}
						/>
						<MasterStatCard
							tone="primary"
							icon={<IconCheck />}
							value={stats.active}
							label={i18n.t("master.dashboard.activeCompanies")}
						/>
						<MasterStatCard
							tone="info"
							icon={<IconUsers />}
							value={stats.totalUsers}
							label={i18n.t("master.dashboard.totalUsers")}
						/>
						<MasterStatCard
							tone="secondary"
							icon={<IconWifi />}
							value={stats.totalConnections}
							label={i18n.t("master.dashboard.totalConnections")}
						/>
					</div>

					<div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
						<div className="card lg:col-span-2">
							<div className="card-header">
								<h3 className="card-title m-0">{i18n.t("master.dashboard.registrationsChart")}</h3>
							</div>
							<div className="card-body h-72">
								{stats.registrationsByMonth.length === 0 ? (
									<p className="text-sm text-default-500">{i18n.t("master.dashboard.noChartData")}</p>
								) : (
									<ResponsiveContainer width="100%" height="100%">
										<BarChart data={stats.registrationsByMonth}>
											<CartesianGrid strokeDasharray="3 3" stroke={chartGrid} />
											<XAxis dataKey="month" tick={{ fill: chartText, fontSize: 12 }} />
											<YAxis allowDecimals={false} tick={{ fill: chartText, fontSize: 12 }} />
											<Tooltip
												contentStyle={{
													background: darkMode ? "#18181b" : "#fff",
													border: `1px solid ${chartGrid}`,
												}}
											/>
											<Bar dataKey="count" fill="#3b82f6" radius={[4, 4, 0, 0]} />
										</BarChart>
									</ResponsiveContainer>
								)}
							</div>
						</div>

						<div className="card">
							<div className="card-header">
								<h3 className="card-title m-0">{i18n.t("master.dashboard.statusChart")}</h3>
							</div>
							<div className="card-body h-72">
								{stats.statusPie.length === 0 ? (
									<p className="text-sm text-default-500">{i18n.t("master.dashboard.noChartData")}</p>
								) : (
									<ResponsiveContainer width="100%" height="100%">
										<PieChart>
											<Pie
												data={stats.statusPie}
												dataKey="value"
												nameKey="name"
												cx="50%"
												cy="50%"
												outerRadius="70%"
												label={({ name, percent }) =>
													`${name} ${(percent * 100).toFixed(0)}%`
												}
											>
												{stats.statusPie.map((_, i) => (
													<Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
												))}
											</Pie>
											<Tooltip />
										</PieChart>
									</ResponsiveContainer>
								)}
							</div>
						</div>

						<div className="card lg:col-span-3">
							<div className="card-header">
								<h3 className="card-title m-0">{i18n.t("master.dashboard.topCompaniesChart")}</h3>
							</div>
							<div className="card-body h-80">
								{stats.topByUsers.length === 0 ? (
									<p className="text-sm text-default-500">{i18n.t("master.dashboard.noChartData")}</p>
								) : (
									<ResponsiveContainer width="100%" height="100%">
										<BarChart data={stats.topByUsers} layout="vertical" margin={{ left: 8 }}>
											<CartesianGrid strokeDasharray="3 3" stroke={chartGrid} />
											<XAxis type="number" tick={{ fill: chartText, fontSize: 12 }} />
											<YAxis
												type="category"
												dataKey="name"
												width={120}
												tick={{ fill: chartText, fontSize: 11 }}
											/>
											<Tooltip
												contentStyle={{
													background: darkMode ? "#18181b" : "#fff",
													border: `1px solid ${chartGrid}`,
												}}
											/>
											<Bar dataKey="users" fill="#8b5cf6" radius={[0, 4, 4, 0]} />
										</BarChart>
									</ResponsiveContainer>
								)}
							</div>
						</div>

						{systemSummary && (
							<div className="card lg:col-span-3">
								<div className="card-header flex flex-wrap items-center justify-between gap-2">
									<h3 className="card-title m-0">{i18n.t("master.dashboard.systemCardTitle")}</h3>
									<Link to={`${MASTER_HOME}/system`} className="btn btn-primary btn-sm">
										{i18n.t("master.dashboard.systemCardLink")}
									</Link>
								</div>
								<div className="card-body grid grid-cols-1 gap-6 sm:grid-cols-3">
									<MasterProgressMetric
										label={i18n.t("master.system.cpu")}
										percent={systemSummary.cpu}
										barClass="bg-primary"
									/>
									<MasterProgressMetric
										label={i18n.t("master.dashboard.memory")}
										percent={systemSummary.ram}
										barClass="bg-success"
									/>
									<MasterProgressMetric
										label={i18n.t("master.dashboard.disk")}
										percent={systemSummary.disk}
										barClass="bg-warning"
									/>
								</div>
							</div>
						)}
					</div>
				</>
			)}
		</div>
	);
}
